import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  DeleteVideoPrivateCommentReplyResult,
  DeleteVideoPrivateCommentReplyOptions,
  PatchVideoPrivateCommentReplyResult,
  PatchVideoPrivateCommentReplyOptions,
  DeleteVideoPrivateCommentReplyFields,
  PatchVideoPrivateCommentReplyFields,
  deleteVideoPrivateCommentReply,
  patchVideoPrivateCommentReply,
} from '../fetchers/video/private_comment/reply';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseDeleteVideoPrivateCommentReplyOptions<Fields extends DeleteVideoPrivateCommentReplyFields> =
  DeleteVideoPrivateCommentReplyOptions<Fields>;

/**
 * Delete a reply to a private comment
 * This method deletes a reply to the specified private comment.
 */

export function useDeleteVideoPrivateCommentReply<
  Fields extends DeleteVideoPrivateCommentReplyFields,
>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<
    DeleteVideoPrivateCommentReplyResult<Fields> | undefined
  >();

  let call = useCallback(
    async (options: UseDeleteVideoPrivateCommentReplyOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/private_comments/${options.where.commentId}/replies/${
            options.where.replyId
          }${serializeQuery(options)}`,
          deleteVideoPrivateCommentReply({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useDeleteVideoPrivateCommentReply, {
    endpoint: '/videos/:videoId/private_comments/:commentId/replies/:replyId',
    method: 'DELETE',
  });
}

type UsePatchVideoPrivateCommentReplyOptions<Fields extends PatchVideoPrivateCommentReplyFields> =
  PatchVideoPrivateCommentReplyOptions<Fields>;

/**
 * Edit a reply to a private comment
 * This method edits the specified reply to a private comment on a video. The authenticated user must be the owner of the reply.
 */

export function usePatchVideoPrivateCommentReply<
  Fields extends PatchVideoPrivateCommentReplyFields,
>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<
    PatchVideoPrivateCommentReplyResult<Fields> | undefined
  >();

  let call = useCallback(
    async (options: UsePatchVideoPrivateCommentReplyOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/private_comments/${options.where.commentId}/replies/${
            options.where.replyId
          }${serializeQuery(options)}`,
          patchVideoPrivateCommentReply({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePatchVideoPrivateCommentReply, {
    endpoint: '/videos/:videoId/private_comments/:commentId/replies/:replyId',
    method: 'PATCH',
  });
}
